<template>
    <div class="box">
        <h2 class="title is-5">
            Publish filter
        </h2>
        <table
            class="table is-narrow is-hoverable is-fullwidth">
            <thead>
                <tr>
                    <th>Library</th>
                    <th>Facilities</th>
                    <th>Filter</th>
                </tr>
            </thead>
            <tbody>
                <tr
                    v-for="row in rows"
                    :key="row.library">
                    <td>{{ row.library }}</td>
                    <td>{{ row.facilities }}</td>
                    <td>{{ filters }}</td>
                </tr>
            </tbody>
        </table>
    </div>
</template>

<script setup lang="ts">
    import { computed, ref, watch } from 'vue';
    import { getLibraries } from '@/shared/helpers/api';
    import { http } from '@/shared/httpWrapper';
    import { longDate } from '@/formatters.ts';
    import { publishesToStidGlobalScope, RECEIVER_STID, STID_GLOBAL_SCOPE } from '@/shared/helpers/publishScope';
    import type { Library, PublishFilter } from '@/shared/helpers/api/types/entities.ts';

    const props = defineProps<{
        spec?: Partial<PublishFilter> | null;
    }>();

    const globalLibraries = ref(new Map<string, Library>());

    // The specification stores the facilities the user picked, but the scope each library is
    // actually published on is resolved at publish time. Only STID resolves to GLOBAL, so no
    // other receiver needs the lookup.
    watch(() => props.spec?.applicationName, async (applicationName) => {
        if (applicationName !== RECEIVER_STID) {
            globalLibraries.value = new Map();
            return;
        }

        try {
            const libraries = await getLibraries(http, '', '', false, false, false, true);
            globalLibraries.value = new Map(libraries.map(lib => [lib.name, lib]));
        } catch {
            // Falling back to the stored facilities beats failing the whole release page.
            globalLibraries.value = new Map();
        }
    }, { immediate: true });

    const selectedFacilities = computed(() => {
        return props.spec?.facilityNames?.join(', ') ?? '';
    });

    function facilitiesFor(library: string): string {
        if (publishesToStidGlobalScope(globalLibraries.value.get(library), props.spec?.applicationName)) {
            return STID_GLOBAL_SCOPE;
        }

        // An empty facility selection is only accepted for GLOBAL publishes, so it never means
        // "no scope at all".
        return selectedFacilities.value || STID_GLOBAL_SCOPE;
    }

    const rows = computed(() => {
        return (props.spec?.libraryNames ?? []).map(library => ({
            library,
            facilities: facilitiesFor(library)
        }));
    });

    const filters = computed(() => {
        const active: string[] = [];
        if (props.spec?.isValid) {
            active.push(`IsValid = ${props.spec.isValid}`);
        }
        if (props.spec?.updatedSince) {
            active.push(`DateUpdated ≥ ${longDate(props.spec.updatedSince)}`);
        }
        return active.join(', ');
    });
</script>
